import { Component, OnInit, TemplateRef, ViewChild, ChangeDetectionStrategy } from "@angular/core";
import { BaseComponent, UserService, ProductService, AdminService } from "@mypxplat/xplat/core";
import { AppService } from "@mypxplat/xplat/web/core";
import { interval, Subject, takeUntil, throttle } from "rxjs";
import { skip } from "rxjs/operators";

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: "myp-giveaway-management",
  templateUrl: "giveaway-management.component.html",
  standalone: false,
})
export class GiveawayManagementComponent extends BaseComponent implements OnInit {
  @ViewChild("addEditGiveawayModal") addEditGiveawayModal: TemplateRef<any>;
  // [animation] on the delete icon binds both fa-icon's AnimationProp and ngbTooltip's boolean input.
  public readonly spinAnimation: any = "spin";

  public loading: boolean = false;
  public saving: boolean = false;
  public giveaways: any[] = [];
  public editing: boolean = false;
  public editingGiveaway: any = {};

  public entitledProductQuery: string = "";
  public entitledProductResults: any[];
  public searchingEntitledProducts: boolean = false;
  public selectedEntitledProduct: any;
  private _entitledQueryStream$: Subject<any> = new Subject();

  public giftProductQuery: string = "";
  public giftProductResults: any[];
  public searchingGiftProducts: boolean = false;
  public selectedGiftProduct: any;
  public selectedGiftProducts: any[] = [];
  private _giftQueryStream$: Subject<any> = new Subject();

  public deletingMap: { [key: number]: boolean } = {};
  public subDataMap: { [key: number]: string[] } = {};

  public page: number = 1;
  public limit: number = 50;
  public total: number = 0;
  public includeExpired: boolean = false;

  constructor(
    userService: UserService,
    public productService: ProductService,
    public adminService: AdminService,
    public appService: AppService
  ) {
    super(userService);
  }

  ngOnInit() {
    this.loadGiveaways();
    this._entitledQueryStream$
      .pipe(
        skip(1),
        throttle(() => interval(1000), { leading: true, trailing: true }),
        takeUntil(this.destroy$)
      )
      .subscribe(() => this.searchEntitledProducts());

    this._giftQueryStream$
      .pipe(
        skip(1),
        throttle(() => interval(1000), { leading: true, trailing: true }),
        takeUntil(this.destroy$)
      )
      .subscribe(() => this.searchGiftProducts());
  }

  loadGiveaways() {
    this.loading = true;
    const params = `product/giveaways?page=${this.page}&limit=${this.limit}&includeExpired=${this.includeExpired}`;
    this.adminService.get(params).subscribe({
      next: (result: any) => {
        this.giveaways = result?.items || [];
        this.total = result?.total || 0;
        this.buildSubDataMap();
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      },
    });
  }

  toggleIncludeExpired() {
    this.includeExpired = !this.includeExpired;
    this.page = 1;
    this.loadGiveaways();
  }

  paginate = (direction: string) => {
    if (direction === "next") {
      this.page++;
    } else if (direction === "previous") {
      this.page--;
    }
    this.loadGiveaways();
  };

  get totalPages(): number {
    return Math.ceil(this.total / this.limit);
  }

  openAddModal() {
    this.editing = false;
    this.editingGiveaway = {
      auto_gift: true,
      allow_customer_claim: false,
      allowMultiple: false,
      date_of_purchase_check: false,
    };
    this.selectedEntitledProduct = null;
    this.selectedGiftProduct = null;
    this.selectedGiftProducts = [];
    this.entitledProductQuery = "";
    this.giftProductQuery = "";
    this.entitledProductResults = undefined;
    this.giftProductResults = undefined;
    this.appService.showModal(this.addEditGiveawayModal, { size: "lg" });
  }

  openEditModal(giveaway: any) {
    this.editing = true;
    this.editingGiveaway = { ...giveaway };
    this.selectedEntitledProduct = { id: giveaway.entitled_id, title: giveaway.entitled_title };
    this.selectedGiftProduct = { id: giveaway.gift_id, title: giveaway.gift_title };
    this.entitledProductQuery = "";
    this.giftProductQuery = "";
    this.entitledProductResults = undefined;
    this.giftProductResults = undefined;
    this.appService.showModal(this.addEditGiveawayModal, { size: "lg" });
  }

  onEntitledQueryChange(query: string) {
    this.entitledProductQuery = query;
    if (query) {
      this._entitledQueryStream$.next(query);
    } else {
      this.entitledProductResults = undefined;
    }
  }

  onGiftQueryChange(query: string) {
    this.giftProductQuery = query;
    if (query) {
      this._giftQueryStream$.next(query);
    } else {
      this.giftProductResults = undefined;
    }
  }

  searchEntitledProducts() {
    if (this.entitledProductQuery) {
      this.searchingEntitledProducts = true;
      this.productService.searchv2("title", this.entitledProductQuery, 5).subscribe((result: any[]) => {
        this.searchingEntitledProducts = false;
        this.entitledProductResults = result;
      });
    }
  }

  searchGiftProducts() {
    if (this.giftProductQuery) {
      this.searchingGiftProducts = true;
      this.productService.searchv2("title", this.giftProductQuery, 5).subscribe((result: any[]) => {
        this.searchingGiftProducts = false;
        this.giftProductResults = result;
      });
    }
  }

  selectEntitledProduct(product: any) {
    this.selectedEntitledProduct = product;
    this.editingGiveaway.entitled_id = product.id;
    this.entitledProductQuery = "";
    this.entitledProductResults = undefined;
  }

  selectGiftProduct(product: any) {
    if (this.editing) {
      this.selectedGiftProduct = product;
      this.editingGiveaway.gift_id = product.id;
    } else {
      if (!this.selectedGiftProducts.find((p) => p.id === product.id)) {
        this.selectedGiftProducts.push(product);
      }
    }
    this.giftProductQuery = "";
    this.giftProductResults = undefined;
  }

  clearEntitledProduct() {
    this.selectedEntitledProduct = null;
    this.editingGiveaway.entitled_id = null;
  }

  clearGiftProduct() {
    this.selectedGiftProduct = null;
    this.editingGiveaway.gift_id = null;
  }

  removeGiftProduct(product: any) {
    this.selectedGiftProducts = this.selectedGiftProducts.filter((p) => p.id !== product.id);
  }

  submit() {
    if (!this.editingGiveaway.entitled_id) return;
    if (this.editing && !this.editingGiveaway.gift_id) return;
    if (!this.editing && !this.selectedGiftProducts.length) return;
    this.saving = true;

    const baseData = {
      entitled_id: this.editingGiveaway.entitled_id,
      startDate: this.editingGiveaway.startDate || null,
      endDate: this.editingGiveaway.endDate || null,
      auto_gift: this.editingGiveaway.auto_gift || false,
      allow_customer_claim: this.editingGiveaway.allow_customer_claim || false,
      allowMultiple: this.editingGiveaway.allowMultiple || false,
      date_of_purchase_check: this.editingGiveaway.date_of_purchase_check || false,
      allowed_countries: this.editingGiveaway.allowed_countries || null,
      key_duration: this.editingGiveaway.key_duration || null,
    };

    const request = this.editing
      ? this.adminService.put(`product/giveaways/${this.editingGiveaway.id}`, { ...baseData, gift_id: this.editingGiveaway.gift_id })
      : this.adminService.post("product/giveaways", { ...baseData, gift_ids: this.selectedGiftProducts.map((p) => p.id) });

    request.subscribe({
      next: () => {
        this.saving = false;
        this.appService.closeModals();
        this.loadGiveaways();
      },
      error: (err: any) => {
        this.saving = false;
        const message = err?.error?.error || err?.error?.message || "An error occurred saving the giveaway.";
        alert(message);
      },
    });
  }

  deleteGiveaway(giveaway: any) {
    this.appService.confirm(`Are you sure you want to delete this giveaway? (${giveaway.entitled_title} → ${giveaway.gift_title})`).then((result) => {
      if (result) {
        this.deletingMap[giveaway.id] = true;
        this.adminService.delete(`product/giveaways/${giveaway.id}`).subscribe({
          next: () => {
            this.giveaways = this.giveaways.filter((g) => g.id !== giveaway.id);
            delete this.deletingMap[giveaway.id];
          },
          error: () => {
            delete this.deletingMap[giveaway.id];
          },
        });
      }
    });
  }

  buildSubDataMap() {
    this.subDataMap = {};
    for (const giveaway of this.giveaways) {
      this.subDataMap[giveaway.id] = [this.getStatusFlags(giveaway), this.formatDate(giveaway.startDate) + " - " + this.formatDate(giveaway.endDate)];
    }
  }

  formatDate(date: string): string {
    if (!date) return "No date";
    return new Date(date).toLocaleDateString();
  }

  getStatusFlags(giveaway: any): string {
    const flags: string[] = [];
    if (giveaway.auto_gift) flags.push("Auto Gift");
    if (giveaway.allow_customer_claim) flags.push("Customer Claim");
    if (giveaway.allowMultiple) flags.push("Allow Multiple");
    if (giveaway.date_of_purchase_check) flags.push("Date Check");
    if (giveaway.allowed_countries) flags.push("Country Restricted");
    return flags.length ? flags.join(", ") : "No flags";
  }
}
